/**
 * Feedback tone enum and prompt instruction helpers (Feedback 7B).
 *
 * Tone affects feedback wording only — never score, rubric criteria, or exports.
 * Canonical TS module; server runtime uses the JS mirror in `server/lib/feedbackTone.js`.
 * Future UI (7C) should import from here.
 */

/** Allow-listed feedback tones. No age/grade-level/protected-attribute tones in 7B. */
export const FEEDBACK_TONES = [
  'default',
  'clear_direct',
  'encouraging',
  'concise',
  'detailed',
  'growth_focused',
] as const;

export type FeedbackTone = (typeof FEEDBACK_TONES)[number];

const TONE_SET = new Set<string>(FEEDBACK_TONES);

/** Shared guardrail appended to every non-default tone instruction. */
export const FEEDBACK_TONE_GUARD_RAIL =
  'Tone changes wording only. Keep score, rubric interpretation, and evidence unchanged.';

const TONE_INSTRUCTIONS: Record<Exclude<FeedbackTone, 'default'>, string> = {
  clear_direct:
    'Use clear, direct, professional feedback. Be specific and easy to act on. Do not change the score or rubric interpretation.',
  encouraging:
    'Use encouraging, supportive feedback while remaining honest and specific. Do not inflate performance, change the score, or weaken rubric alignment.',
  concise:
    'Use concise feedback with fewer words while preserving the most important evidence and next step. Do not omit essential rubric-based reasoning.',
  detailed:
    'Use more detailed feedback that explains the evidence and next step clearly. Do not add unsupported claims or change the score.',
  growth_focused:
    'Use growth-focused feedback that emphasizes the next step for improvement. Do not change the score, rubric criteria, or evidence.',
};

/**
 * Normalize an unknown tone value to the allow-list. Unknown/null/missing → `default`.
 * No arbitrary user-provided tone text is accepted.
 */
export function normalizeFeedbackTone(value: unknown): FeedbackTone {
  if (typeof value === 'string' && TONE_SET.has(value)) {
    return value as FeedbackTone;
  }
  return 'default';
}

/** True when non-default tone instructions may be appended to the grading prompt. */
export function isFeedbackTonePromptEnabled(): boolean {
  return process.env.FEEDBACK_TONE_PROMPT_ENABLED === 'true';
}

/**
 * Build an isolated tone instruction for the grading prompt.
 * `default` returns empty string (implicit style remains in the JSON schema line).
 */
export function buildToneInstruction(tone: FeedbackTone): string {
  if (tone === 'default') return '';
  const specific = TONE_INSTRUCTIONS[tone];
  return `${specific} ${FEEDBACK_TONE_GUARD_RAIL} Do not invent evidence. Keep feedback based on student work and the rubric.`;
}

/** Legacy implicit feedback style line embedded in the JSON schema (unchanged in 7B). */
export const LEGACY_FEEDBACK_SCHEMA_HINT =
  'friendly personalized feedback using student first name, strength + growth + next step, max 120 words';

export interface GradeSubmissionPromptInput {
  rubricText: string;
  maxScore: number;
  studentName: string;
  /** Optional tone; normalized via {@link normalizeFeedbackTone}. */
  feedbackTone?: unknown;
}

/**
 * Build the text part of the grading multimodal prompt.
 * Default / flag-off / `default` tone: identical to the pre-7B `gradeSubmission` prompt.
 */
export function buildGradeSubmissionPromptText(input: GradeSubmissionPromptInput): string {
  const { rubricText, maxScore, studentName } = input;
  const tone = normalizeFeedbackTone(input.feedbackTone);

  const base = `Grade this student work for ${studentName}. Max score: ${maxScore}.
Rubric:
${rubricText}

Return JSON only:
{
  "score": number,
  "maxScore": ${maxScore},
  "criterionScores": [{"criterionId":"c1","label":"string","points":number,"rationale":"string"}],
  "feedback": "${LEGACY_FEEDBACK_SCHEMA_HINT}"
}`;

  if (!isFeedbackTonePromptEnabled() || tone === 'default') {
    return base;
  }

  const toneInstruction = buildToneInstruction(tone);
  if (!toneInstruction) return base;

  return `${base}

Feedback tone instruction: ${toneInstruction}`;
}
